import { useCallback, useEffect, useState } from 'react';
import { pbAuthedFetch } from "../authHeaders";

// The run archive, and sharing out of it.

interface Run {
  id: string;
  type_label: string;
  period_label: string;
  scope_label: string;
  format: string;
  byte_size: number;
  created_at: string;
  generated_by: string;
  has_content: boolean;
  filename: string;
}

interface MintedShare {
  runId: string;
  url: string;
  expiresAt: string;
  shareId: string;
}

interface ReportHistoryProps {
  maxShareDays: number;
  showToast: (msg: string) => void;
}

const when = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso || '');
  }
};

const kb = (n: number) =>
  `${Math.max(1, Math.round(Number(n || 0) / 1024)).toLocaleString('en-US')} KB`;

export function ReportHistory({ maxShareDays, showToast }: ReportHistoryProps) {
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [minted, setMinted] = useState<MintedShare | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    pbAuthedFetch('/api/reports/runs?limit=25')
      .then((res) => (res && res.ok ? res.json() : null))
      .then((body) => setRuns((body && body.runs) || []))
      .catch(() => setRuns([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const download = useCallback(
    async (run: Run) => {
      setBusy(`download:${run.id}`);
      try {
        const res = await pbAuthedFetch(`/api/reports/runs/${run.id}/download`);
        if (!res || !res.ok) {
          const payload = res ? await res.json().catch(() => ({})) : {};
          throw new Error(
            payload.error === 'content_not_retained'
              ? 'This report was too large to keep — generate it again'
              : payload.error || 'Could not fetch it'
          );
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = run.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        showToast((err as Error).message);
      } finally {
        setBusy(null);
      }
    },
    [showToast]
  );

  const share = useCallback(
    async (run: Run) => {
      setBusy(`share:${run.id}`);
      try {
        const res = await pbAuthedFetch(`/api/reports/runs/${run.id}/share`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ days: Math.min(7, maxShareDays) }),
        });
        const payload = res ? await res.json().catch(() => ({})) : {};
        if (!res || !res.ok) throw new Error(payload.error || 'Could not create the link');
        setMinted({
          runId: run.id,
          url: new URL(payload.url, window.location.origin).toString(),
          expiresAt: payload.share.expiresAt,
          shareId: payload.share.id,
        });
      } catch (err) {
        showToast((err as Error).message);
      } finally {
        setBusy(null);
      }
    },
    [maxShareDays, showToast]
  );

  const copy = useCallback(async () => {
    if (!minted) return;
    try {
      await navigator.clipboard.writeText(minted.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast('Could not copy — select the link and copy it manually');
    }
  }, [minted, showToast]);

  const revoke = useCallback(
    async (shareId: string) => {
      const res = await pbAuthedFetch(`/api/reports/shares/${shareId}`, { method: 'DELETE' });
      if (res && res.ok) {
        setMinted(null);
        showToast('Link revoked');
      } else {
        showToast('Could not revoke the link');
      }
    },
    [showToast]
  );

  if (runs === null)
    return (
      <div className="state">
        <p className="state__body">Loading history…</p>
      </div>
    );
  if (!runs.length)
    return (
      <div className="state">
        <p className="state__body">Nothing generated yet. Downloads are archived here.</p>
      </div>
    );

  return (
    <div className="rpt-history">
      {minted && (
        <div className="rpt-minted" role="status">
          <p className="rpt-label">Share link — copy it now</p>
          <input
            readOnly
            value={minted.url}
            onFocus={(e) => e.target.select()}
            aria-label="Share link"
          />
          <p className="rpt-hint">
            Expires {when(minted.expiresAt)}. This link is shown once — only a hash of it is
            stored, so it cannot be read again.
          </p>
          <div className="rpt-buttons">
            <button type="button" className="btn btn--ghost btn--sm" onClick={copy}>
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => revoke(minted.shareId)}
            >
              Revoke
            </button>
          </div>
        </div>
      )}
      <ul className="rpt-runs">
        {runs.map((r) => {
          const downloadBusy = busy === `download:${r.id}`;
          const shareBusy = busy === `share:${r.id}`;
          const rowBusy = downloadBusy || shareBusy;
          return (
            <li key={r.id}>
              <div className="rpt-run-main">
                <span className="rpt-run-t">{r.type_label}</span>
                <span className="rpt-run-m">
                  {r.period_label} · {r.scope_label} · {String(r.format).toUpperCase()} ·{' '}
                  {kb(r.byte_size)}
                </span>
                <span className="rpt-run-m">
                  {when(r.created_at)} · {r.generated_by}
                </span>
              </div>
              <div className="rpt-run-act">
                {r.has_content ? (
                  <>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={rowBusy}
                      aria-busy={downloadBusy}
                      onClick={() => download(r)}
                    >
                      {downloadBusy ? 'Downloading…' : 'Download'}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={rowBusy}
                      aria-busy={shareBusy}
                      onClick={() => share(r)}
                    >
                      {shareBusy ? 'Sharing…' : 'Share'}
                    </button>
                  </>
                ) : (
                  <span className="rpt-hint">Too large to retain</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
